import { CSSProperties, FormEventHandler, memo, ReactNode, useCallback } from 'react'
import cn from 'classnames'
import { IconSpinner } from 'components/IconSpinner'
import { KeyCodeEnum } from 'enums/keyCodes'
import { useHotKey } from 'hooks/useHotKey'
import { stopBubbling } from 'utils/event'

import { DialogMobile } from '../DialogMobile'

interface IProps {
  children: ReactNode
  action?: ReactNode
  className?: string
  /** Слот для нижнего блока мобильного диалога */
  footer?: ReactNode
  footerClassName?: string
  formName?: string
  /** Слот для верхнего левого блока мобильного диалога */
  header?: ReactNode
  /** Слот для верхнего правого блока мобильного диалога */
  headerAdditional?: ReactNode
  id?: string
  /** Закрытие окна по нажатию на Esc */
  isCloseOnEsc?: boolean
  /** Закрытие окна по тапу вне области этого окна */
  isCloseOnOutsideClick?: boolean
  /** Отображение окна как диалогового (прижатого к низу) */
  isDialogType?: boolean
  /** Отображение загрузки данных окна */
  isLoading?: boolean
  /** Отображение горизонтальных и вертикальных отступов контентной части */
  isOffsets?: boolean
  /** Отображение кнопки закрытия окна */
  isShowCloseButton?: boolean
  onClose?: () => void
  onSubmit?: FormEventHandler<HTMLFormElement>
  style?: CSSProperties
}

export const ModalMobile = memo<IProps>(function ModalMobile({
  action,
  children,
  className,
  footerClassName,
  footer,
  formName,
  header,
  headerAdditional,
  id,
  isCloseOnEsc = true,
  isCloseOnOutsideClick = true,
  isShowCloseButton = true,
  isDialogType = false,
  isLoading = false,
  isOffsets = true,
  onClose,
  onSubmit,
  style
}) {
  const handlePressOnEsc = useCallback(() => {
    if (isCloseOnEsc && onClose) {
      onClose()
    }
  }, [isCloseOnEsc, onClose])

  useHotKey(KeyCodeEnum.ESC, handlePressOnEsc)

  const content = () => {
    if (isLoading) {
      return <IconSpinner size="nm" />
    }

    return (
      <DialogMobile
        action={action}
        footer={footer}
        footerClassName={footerClassName}
        header={header}
        headerAdditional={headerAdditional}
        id={id}
        isCloseOnOutsideClick={isCloseOnOutsideClick}
        isDialogType={isDialogType}
        isOffsets={isOffsets}
        isShowCloseButton={isShowCloseButton}
        onClose={onClose}
        className={cn({
          'w-full before:fixed before:left-0 before:top-0 before:z-[1] before:h-[100vh] before:w-[100vw] before:bg-[rgba(26,28,30,0.3)] before:content-[""]':
            isDialogType,
          'size-full': !isDialogType
        })}
      >
        {children}
      </DialogMobile>
    )
  }

  const isForm = !!onSubmit
  const props = {
    className: cn('w-full fixed top-0 z-modal', className, {
      'flex items-end': isDialogType,
      'flex flex-col items-center justify-center': isLoading
    }),
    id,
    onClick: stopBubbling,
    style
  }

  return isForm ? (
    <form {...props} name={formName} onSubmit={onSubmit}>
      {content()}
    </form>
  ) : (
    <div {...props}>{content()}</div>
  )
})
